import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  CompanyProfile,
  fetchCompanyProfile,
  formatCurrency,
  getDefaultCompanyProfile
} from '../Services/companyProfileService';

interface CompanyProfileContextType {
  companyProfile: CompanyProfile | null;
  loading: boolean;
  error: string | null;
  formatPrice: (amount: number) => string;
  currencySymbol: string;
  currency: string;
  displayPrices: boolean;
  refreshProfile: () => Promise<void>;
}

const CompanyProfileContext = createContext<CompanyProfileContextType | undefined>(undefined);

interface CompanyProfileProviderProps {
  children: ReactNode;
}

export const CompanyProfileProvider: React.FC<CompanyProfileProviderProps> = ({ children }) => {
  const [companyProfile, setCompanyProfile] = useState<CompanyProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadCompanyProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const profile = await fetchCompanyProfile();
      
      if (profile) {
        setCompanyProfile(profile);
      } else {
        // Use default profile if none found
        setCompanyProfile(getDefaultCompanyProfile());
      }
    } catch (err) {
      console.error('Error loading company profile:', err);
      setError('Failed to load company profile');
      // Use default profile on error
      setCompanyProfile(getDefaultCompanyProfile());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompanyProfile();
  }, []);

  const formatPrice = (amount: number): string => {
    if (!companyProfile) {
      return `$${amount.toFixed(2)}`;
    }
    return formatCurrency(amount, companyProfile.currency);
  };

  const currencySymbol = companyProfile?.currency || '$';
  const currency = companyProfile?.currency || 'USD';
  const displayPrices = companyProfile?.displayPrices !== false;

  const refreshProfile = async () => {
    await loadCompanyProfile();
  };

  return (
    <CompanyProfileContext.Provider
      value={{
        companyProfile,
        loading,
        error,
        formatPrice,
        currencySymbol,
        currency,
        displayPrices,
        refreshProfile
      }}
    >
      {children}
    </CompanyProfileContext.Provider>
  );
};

// Custom hook to use the company profile context
export const useCompanyProfile = (): CompanyProfileContextType => {
  const context = useContext(CompanyProfileContext);
  
  if (context === undefined) {
    throw new Error('useCompanyProfile must be used within a CompanyProfileProvider');
  }
  
  return context;
};